<template>
  <v-container fluid class="pa-6 pa-md-8">
    <div class="page-head d-flex flex-wrap align-center justify-space-between">
      <div>
        <h1 class="page-head__title">{{ greeting }}</h1>
        <p class="page-head__sub">
          Here's what's happening across the credit union today.
        </p>
      </div>
      <v-btn
        outlined
        class="toolbar-btn text-none"
        :loading="pageLoading"
        @click="loadAll"
      >
        <v-icon small left>mdi-refresh</v-icon>
        Refresh
      </v-btn>
    </div>

    <!-- KPI tiles -->
    <v-row class="mb-1">
      <v-col
        v-for="card in cards"
        :key="card.description"
        cols="12"
        sm="6"
        lg="3"
      >
        <information-card
          :icon="card.icon"
          :description="card.description"
          :total="card.total"
        />
      </v-col>
    </v-row>

    <!-- Charts -->
    <v-row>
      <v-col cols="12" lg="8">
        <div class="panel">
          <div class="panel__head">
            <v-icon small class="mr-2">mdi-chart-areaspline</v-icon>
            Transaction Activity
            <span class="panel__badge">last {{ trendDays }} days</span>
          </div>
          <div class="panel__body">
            <apexchart
              v-if="hasTrend"
              type="area"
              height="300"
              :options="trendOptions"
              :series="trendSeries"
            ></apexchart>
            <div v-else-if="pageLoading" class="panel__empty">
              <v-progress-circular indeterminate color="primary" />
            </div>
            <div v-else class="panel__empty text-muted">
              No transaction activity yet
            </div>
          </div>
        </div>
      </v-col>

      <v-col cols="12" lg="4">
        <div class="panel">
          <div class="panel__head">
            <v-icon small class="mr-2">mdi-podium-gold</v-icon>
            Top MSISDNs
          </div>
          <div class="panel__body">
            <apexchart
              v-if="hasTopMsisdns"
              type="bar"
              height="300"
              :options="topMsisdnsOptions"
              :series="topMsisdnsSeries"
            ></apexchart>
            <div v-else-if="pageLoading" class="panel__empty">
              <v-progress-circular indeterminate color="primary" />
            </div>
            <div v-else class="panel__empty text-muted">No data available</div>
          </div>
        </div>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12" lg="5">
        <div class="panel">
          <div class="panel__head">
            <v-icon small class="mr-2">mdi-account-clock-outline</v-icon>
            Customer Lifecycle
            <v-spacer />
            <v-menu
              ref="lifecycleMenu"
              v-model="lifecycleMenu"
              :close-on-content-click="false"
              :return-value.sync="lifecycleDates"
              transition="scale-transition"
              offset-y
              min-width="auto"
              left
            >
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  outlined
                  x-small
                  v-bind="attrs"
                  v-on="on"
                  class="lifecycle-date-btn text-none"
                  :class="{
                    'lifecycle-date-btn--active': lifecycleRange.length === 2
                  }"
                >
                  <v-icon x-small left>mdi-calendar-range-outline</v-icon>
                  {{
                    lifecycleRange.length === 2 ? lifecycleRangeText : "All time"
                  }}
                  <v-icon
                    v-if="lifecycleRange.length === 2"
                    x-small
                    right
                    @click.stop="clearLifecycleDates"
                    >mdi-close</v-icon
                  >
                </v-btn>
              </template>
              <v-date-picker
                v-model="lifecycleDates"
                range
                color="primary"
                :max="today"
              >
                <v-spacer></v-spacer>
                <v-btn small text color="primary" @click="lifecycleMenu = false"
                  >Cancel</v-btn
                >
                <v-btn small text color="primary" @click="applyLifecycleDates"
                  >OK</v-btn
                >
              </v-date-picker>
            </v-menu>
          </div>
          <div class="panel__body">
            <apexchart
              v-if="hasLifecycle"
              type="donut"
              height="300"
              :options="lifecycleOptions"
              :series="lifecycleSeries"
            ></apexchart>
            <div v-else-if="pageLoading" class="panel__empty">
              <v-progress-circular indeterminate color="primary" />
            </div>
            <div v-else class="panel__empty text-muted">No data available</div>
          </div>
        </div>
      </v-col>
      <v-col cols="12" lg="7">
        <div class="panel lifecycle-summary">
          <div class="panel__head">
            <v-icon small class="mr-2">mdi-chart-donut</v-icon>
            Lifecycle summary
          </div>
          <div class="lifecycle-summary__body">
            <div
              v-for="row in lifecycleRows"
              :key="row.label"
              class="lifecycle-row"
            >
              <span class="lifecycle-dot" :style="{ background: row.color }" />
              <span class="lifecycle-row__label">{{ row.label }}</span>
              <span class="lifecycle-row__value tabular">{{ row.value }}</span>
              <span class="lifecycle-row__pct tabular">{{ row.pct }}%</span>
            </div>
            <div class="lifecycle-row lifecycle-row--tenure">
              <v-icon small class="mr-2">mdi-timer-sand</v-icon>
              <span class="lifecycle-row__label">Avg. tenure</span>
              <span class="lifecycle-row__value tabular">{{ avgTenure }} days</span>
            </div>
          </div>
        </div>
      </v-col>
    </v-row>

    <!-- Recent transactions -->
    <div class="mt-4">
      <div class="d-flex align-center mb-3">
        <h2 class="section-title mb-0">Recent transactions</h2>
        <v-spacer />
        <router-link
          :to="{ name: 'admin.transactions.all' }"
          class="view-all"
        >
          View all
          <v-icon small>mdi-arrow-right</v-icon>
        </router-link>
      </div>
      <v-data-table
        :items="recent"
        :headers="recentHeaders"
        :loading="pageLoading"
        class="data-table"
        hide-default-footer
        :items-per-page="6"
        loading-text="Loading… Please wait"
        no-data-text="No recent transactions"
      >
        <template #item.createdAt="{ item }">
          <span class="text-muted">{{ item.createdAt | dateFormat }}</span>
        </template>
        <template #item.name="{ item }">
          <span
            v-if="item.meta && item.meta.accountName"
            class="cell-strong text-capitalize"
            >{{ item.meta.accountName }}</span
          >
          <span v-else class="text-muted">N/A</span>
        </template>
        <template #item.totalAmount="{ item }">
          <span class="tabular cell-strong"
            >GHC {{ item.totalAmount ? item.totalAmount : "N/A" }}</span
          >
        </template>
        <template #item.service="{ item }">
          <span class="pill pill--brand text-uppercase">{{
            item.meta && (item.meta.product || item.meta.name)
          }}</span>
        </template>
        <template #item.paymentStatus="{ item }">
          <span
            v-if="
              item.paymentStatus &&
                item.paymentStatus.toLowerCase() === 'success'
            "
            class="pill pill--success text-uppercase"
            >{{ item.paymentStatus }}</span
          >
          <span
            v-else-if="
              item.paymentStatus &&
                item.paymentStatus.toLowerCase() === 'failed'
            "
            class="pill pill--failed text-uppercase"
            >{{ item.paymentStatus }}</span
          >
          <span v-else class="pill pill--pending text-uppercase">{{
            item.paymentStatus ? item.paymentStatus : "Pending"
          }}</span>
        </template>
      </v-data-table>
    </div>
  </v-container>
</template>

<script>
import InformationCard from "@/components/component/InformationCard";
import { mapGetters } from "vuex";
import moment from "moment";

export default {
  components: { InformationCard },
  data() {
    return {
      trendDays: 30,
      lifecycleMenu: false,
      lifecycleDates: [],
      lifecycleRange: [],
      today: moment().format("YYYY-MM-DD")
    };
  },
  computed: {
    ...mapGetters({
      pageLoading: "getIsPageLoading",
      profile: "auth/getUserDetails",
      totalCount: "reports/getTotalCount",
      statistics: "reports/getTransactionStatistics",
      topMsisdns: "reports/getTopMsisdns",
      lifecycle: "reports/getMsisdnLifecycle",
      recent: "transactions/getAllTransactions"
    }),
    lifecycleGroups() {
      const g = {};
      (this.lifecycle || []).forEach(d => {
        const s = d.status || "Unknown";
        g[s] = (g[s] || 0) + 1;
      });
      return g;
    },
    lifecycleColor() {
      return {
        Active: "#0f9d63",
        Dormant: "#d9a406",
        Unknown: "#6b7280"
      };
    },
    hasLifecycle() {
      return (this.lifecycle || []).length > 0;
    },
    lifecycleSeries() {
      return Object.values(this.lifecycleGroups);
    },
    lifecycleRows() {
      const total = (this.lifecycle || []).length || 1;
      return Object.keys(this.lifecycleGroups).map(k => ({
        label: k,
        value: this.lifecycleGroups[k],
        pct: Math.round((this.lifecycleGroups[k] / total) * 100),
        color: this.lifecycleColor[k] || "#6b7280"
      }));
    },
    avgTenure() {
      const list = this.lifecycle || [];
      if (!list.length) return 0;
      const sum = list.reduce((s, d) => s + (d.tenureDays || 0), 0);
      return Math.round(sum / list.length);
    },
    lifecycleRangeText() {
      if (this.lifecycleRange.length !== 2) return "";
      const [s, e] = this.lifecycleRange;
      return `${moment(s).format("MMM DD")} – ${moment(e).format("MMM DD")}`;
    },
    lifecycleOptions() {
      const labels = Object.keys(this.lifecycleGroups);
      return {
        chart: { type: "donut", background: "transparent" },
        theme: { mode: this.chartTheme.mode },
        labels,
        colors: labels.map(l => this.lifecycleColor[l] || "#6b7280"),
        stroke: { colors: ["transparent"] },
        legend: { position: "bottom", labels: { colors: this.chartTheme.foreColor } },
        dataLabels: { formatter: val => `${Math.round(val)}%` },
        plotOptions: {
          pie: {
            donut: {
              size: "68%",
              labels: {
                show: true,
                total: {
                  show: true,
                  label: "MSISDNs",
                  color: this.chartTheme.foreColor,
                  formatter: () => (this.lifecycle || []).length
                }
              }
            }
          }
        },
        tooltip: { theme: this.chartTheme.mode }
      };
    },
    greeting() {
      const hour = new Date().getHours();
      const part =
        hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";
      const name =
        this.profile && this.profile.name
          ? this.profile.name.split(" ")[0]
          : "";
      return name ? `${part}, ${name}` : part;
    },
    volume30d() {
      const total = (this.statistics || []).reduce(
        (sum, item) => sum + (item.totalAmount || 0),
        0
      );
      return this.compactCurrency(total);
    },
    cards() {
      return [
        {
          icon: "mdi-account-multiple-outline",
          description: "Total Members",
          total: this.totalCount.membersCount
        },
        {
          icon: "mdi-account-tie-outline",
          description: "Total Staff",
          total: this.totalCount.staffCount
        },
        {
          icon: "mdi-swap-horizontal",
          description: "Total Transactions",
          total: this.totalCount.totalTransactions
        },
        {
          icon: "mdi-cash-multiple",
          description: "Volume · last 30 days",
          total: this.volume30d
        }
      ];
    },
    isDark() {
      return this.$vuetify.theme.dark;
    },
    chartTheme() {
      return {
        foreColor: this.isDark ? "#9aa3ae" : "#545d68",
        grid: this.isDark ? "rgba(255,255,255,0.08)" : "#e4e7eb",
        mode: this.isDark ? "dark" : "light"
      };
    },
    sortedStats() {
      return [...(this.statistics || [])].sort((a, b) =>
        String(a.date).localeCompare(String(b.date))
      );
    },
    hasTrend() {
      // Gate on data, not the global loading flag — loadAll fires several
      // actions that each toggle pageLoading, so keying the chart on it would
      // unmount/remount ApexCharts mid-refresh and leave it rendering stale.
      return this.sortedStats.length > 0;
    },
    trendSeries() {
      return [
        {
          name: "Transactions",
          data: this.sortedStats.map(item => item.totalTransactions || 0)
        }
      ];
    },
    trendOptions() {
      return {
        chart: {
          type: "area",
          height: 300,
          background: "transparent",
          foreColor: this.chartTheme.foreColor,
          toolbar: { show: false },
          zoom: { enabled: false }
        },
        theme: { mode: this.chartTheme.mode },
        colors: ["#ff0055"],
        dataLabels: { enabled: false },
        stroke: { curve: "smooth", width: 3 },
        fill: {
          type: "gradient",
          gradient: {
            shadeIntensity: 1,
            opacityFrom: 0.35,
            opacityTo: 0.02,
            stops: [0, 95, 100]
          }
        },
        markers: { size: 0, hover: { size: 5 } },
        xaxis: {
          // Pre-format the categories rather than using a label formatter:
          // on a category axis Apex hands the formatter the point index for
          // the tooltip/crosshair, which moment() reads as the epoch.
          categories: this.sortedStats.map(item =>
            moment(item.date).format("MMM DD")
          ),
          labels: {
            rotate: 0,
            hideOverlappingLabels: true
          },
          axisBorder: { show: false },
          axisTicks: { show: false }
        },
        yaxis: {
          labels: {
            formatter: value => Math.round(value)
          }
        },
        tooltip: {
          theme: this.chartTheme.mode,
          x: {
            formatter: (value, opts) => {
              const stat = this.sortedStats[(opts || {}).dataPointIndex];
              return stat
                ? moment(stat.date).format("MMMM DD, YYYY")
                : String(value);
            }
          },
          y: { formatter: value => `${value} transactions` }
        },
        grid: { borderColor: this.chartTheme.grid, strokeDashArray: 4 }
      };
    },
    hasTopMsisdns() {
      return (this.topMsisdns || []).length > 0;
    },
    topMsisdnsSeries() {
      return [
        {
          name: "Total Amount (GHS)",
          data: (this.topMsisdns || []).map(item => item.totalAmount || 0)
        }
      ];
    },
    topMsisdnsOptions() {
      return {
        chart: {
          type: "bar",
          height: 300,
          background: "transparent",
          foreColor: this.chartTheme.foreColor,
          toolbar: { show: false }
        },
        theme: { mode: this.chartTheme.mode },
        colors: ["#ff0055"],
        plotOptions: {
          bar: { borderRadius: 4, horizontal: true, barHeight: "60%" }
        },
        dataLabels: { enabled: false },
        xaxis: {
          categories: (this.topMsisdns || []).map(item => item.msisdn || ""),
          tickAmount: 4,
          labels: {
            rotate: -35,
            rotateAlways: true,
            hideOverlappingLabels: true,
            trim: false,
            style: { fontSize: "11px" },
            formatter: value => this.compactCurrency(value)
          },
          axisBorder: { show: false },
          axisTicks: { show: false }
        },
        tooltip: {
          theme: this.chartTheme.mode,
          y: { formatter: value => `GHS ${Number(value).toFixed(2)}` }
        },
        grid: { borderColor: this.chartTheme.grid, strokeDashArray: 4 }
      };
    },
    recentHeaders() {
      return [
        { text: "Date", value: "createdAt", sortable: false },
        { text: "Acc. Name", value: "name", sortable: false },
        { text: "Amount (GHS)", value: "totalAmount", sortable: false },
        { text: "Service", value: "service", sortable: false },
        { text: "Status", value: "paymentStatus", sortable: false }
      ];
    }
  },
  filters: {
    dateFormat(value) {
      return moment(value).format("Do MMM, HH:mm");
    }
  },
  methods: {
    compactCurrency(value) {
      const num = parseFloat(value || 0);
      if (num >= 1000000) return `GHS ${(num / 1000000).toFixed(1)}M`;
      if (num >= 1000) return `GHS ${(num / 1000).toFixed(1)}K`;
      return `GHS ${num.toFixed(0)}`;
    },
    loadAll() {
      this.$store.dispatch("reports/totalCount");
      this.$store.dispatch("reports/getTransactionStatisticsByDate", {
        page: 1,
        size: this.trendDays
      });
      this.$store.dispatch("reports/getTopMsisdns", { limit: 5 });
      this.fetchLifecycle();
      this.$store.dispatch("transactions/list", "?page=1&size=6");
    },
    fetchLifecycle() {
      const payload = {};
      if (this.lifecycleRange.length === 2) {
        const [startDate, endDate] = this.lifecycleRange;
        payload.startDate = startDate;
        payload.endDate = endDate;
      }
      this.$store.dispatch("reports/getMsisdnLifecycle", payload);
    },
    applyLifecycleDates() {
      this.$refs.lifecycleMenu.save(this.lifecycleDates);
      this.lifecycleRange = [...this.lifecycleDates].sort();
      this.fetchLifecycle();
    },
    clearLifecycleDates() {
      this.lifecycleDates = [];
      this.lifecycleRange = [];
      this.fetchLifecycle();
    }
  },
  created() {
    this.loadAll();
  }
};
</script>

<style scoped>
.toolbar-btn {
  border-color: var(--border) !important;
  color: var(--muted-foreground) !important;
  border-radius: var(--radius) !important;
  height: 40px !important;
  font-weight: 500;
}
.panel {
  background: var(--card);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  box-shadow: var(--shadow-card);
  overflow: hidden;
  height: 100%;
}
.panel__head {
  display: flex;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid var(--border);
  font-family: var(--font-heading);
  font-weight: 600;
  font-size: 15px;
  color: var(--foreground);
}
.lifecycle-date-btn {
  border-color: var(--border) !important;
  color: var(--muted-foreground) !important;
  border-radius: 999px !important;
  height: 28px !important;
  font-weight: 500;
}
.lifecycle-date-btn--active {
  border-color: var(--signal) !important;
  color: var(--signal) !important;
}
.lifecycle-summary__body {
  padding: 12px 20px 16px;
}
.lifecycle-row {
  display: flex;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid var(--border);
}
.lifecycle-row:last-child {
  border-bottom: none;
}
.lifecycle-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  margin-right: 10px;
}
.lifecycle-row__label {
  font-size: 14px;
  font-weight: 500;
  color: var(--foreground);
}
.lifecycle-row__value {
  margin-left: auto;
  font-family: var(--font-mono);
  font-weight: 600;
  color: var(--foreground);
}
.lifecycle-row__pct {
  width: 48px;
  text-align: right;
  font-family: var(--font-mono);
  font-size: 13px;
  color: var(--muted-foreground);
}
.lifecycle-row--tenure .v-icon {
  color: var(--muted-foreground);
}
.panel__badge {
  margin-left: auto;
  font-family: var(--font-sans);
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--muted-foreground);
  background: var(--muted);
  border-radius: 999px;
  padding: 3px 10px;
}
.panel__body {
  padding: 12px 12px 4px;
}
.panel__empty {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 280px;
}
.cell-strong {
  font-weight: 600;
  color: var(--foreground);
}
.text-muted {
  color: var(--muted-foreground);
}
.view-all {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;
  font-weight: 600;
  color: var(--signal);
  text-decoration: none;
}
.view-all .v-icon {
  color: var(--signal);
}
</style>
